import type { CSSProperties } from '@emotion/serialize';
import { styled } from '@sds-system/system';
import type { Theme } from '@sds-system/types';
import { getAllStyledProps } from '@sds-system/utils';
import { processNestedCSS } from '@sds-system/utils/styledUtils/nestedCSS';
import type {
  ChipEndIconWrapperStyledProps,
  ChipProps,
  ChipRootStyledComponentProps,
  ChipStartIconWrapperStyledProps,
  ChipTextWrapperStyledProps,
  RootVariantsType,
} from './Chip.types';

export const StyledChipRoot = styled('div', {
  componentName: 'Chip',
  slot: 'root',
})<ChipRootStyledComponentProps>(
  ({ theme, variant, selected, activeStyles }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: '100%',
    width: 'max-content',
    variants: [
      ...rootVariantStyles(theme, selected || false, activeStyles),
      ...sizeStyles(theme),
      { props: { isRound: true }, style: { borderRadius: '999px' } },
      { props: { hasIcon: true }, style: { gap: '8px' } },
      { props: { isClickable: true }, style: { cursor: 'pointer' } },
      {
        props: { disabled: true },
        style: { cursor: 'not-allowed', ...getDisabledStyle(variant, theme) },
      },
    ],
  }),
  getAllStyledProps,
);

export const StyledChipTextWrapper = styled('div', {
  componentName: 'Chip',
  slot: 'text',
})<ChipTextWrapperStyledProps>(
  { display: 'flex', alignItems: 'center', justifyContent: 'center', whiteSpace: 'nowrap' },
  getAllStyledProps,
);
export const StyledChipStartIconWrapper = styled('div', {
  componentName: 'Chip',
  slot: 'startIcon',
})<ChipStartIconWrapperStyledProps>(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: '100%',
    width: 'min-content',
  },
  ({ theme }) => ({
    variants: [
      {
        props: {
          props: { variant: 'line' },
          style: {
            color: theme.palette.icon.tertiary,
          },
        },
      },
    ],
  }),
);

export const StyledChipEndIconWrapper = styled('div', {
  componentName: 'Chip',
  slot: 'endIcon',
})<ChipEndIconWrapperStyledProps>(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    height: '100%',
    width: 'min-content',
  },
  () => ({
    variants: [
      { props: { isClickable: true }, style: { cursor: 'pointer' } },
      { props: { disabled: true }, style: { cursor: 'not-allowed' } },
    ],
  }),
  getAllStyledProps,
);

const getDisabledStyle = (variant: ChipProps['variant'], theme: Theme) => {
  switch (variant) {
    case 'line':
      return processNestedCSS(
        {
          backgroundColor: theme.palette.bg.disabledLight,
          color: theme.palette.text.disabled,
          borderColor: theme.palette.border.disabled,
        },
        theme,
      );
    case 'solid':
      return processNestedCSS(
        {
          backgroundColor: theme.palette.bg.disabledDark,
        },
        theme,
      );
    case 'solid.light':
      return processNestedCSS(
        {
          backgroundColor: theme.palette.bg.disabledLight,
          color: theme.palette.text.disabled,
        },
        theme,
      );
  }
};

const rootVariantStyles: (
  theme: Theme,
  selected: boolean,
  activeStyles?: CSSProperties,
) => RootVariantsType = (theme, selected, activeStyles) => {
  return [
    {
      props: { variant: 'solid' },
      style: {
        backgroundColor: theme.palette.bg.interactive.primary,
        color: theme.palette.text.interactive.inverse,
        ...(selected &&
          (activeStyles
            ? activeStyles
            : {
                backgroundColor: theme.palette.bg.interactive.primaryPressed,
              })),
      },
    },
    {
      props: { variant: 'solid.light' },
      style: {
        backgroundColor: theme.palette.primary[50],
        color: theme.palette.text.interactive.primary,
        border: `1px solid ${theme.palette.bg.dimmer[0]}`,
        ...(selected &&
          (activeStyles
            ? activeStyles
            : {
                border: `1px solid ${theme.palette.border.interactive.selected}`,
              })),
      },
    },
    {
      props: { variant: 'line' },
      style: {
        color: theme.palette.text.secondary,
        border: `1px solid ${theme.palette.border.secondary}`,
        backgroundColor: theme.palette.bg.primary,
        ...(selected &&
          (activeStyles
            ? activeStyles
            : {
                color: theme.palette.text.interactive.primary,
                borderColor: theme.palette.border.interactive.selected,
                backgroundColor: theme.palette.bg.interactive.selected,
              })),
      },
    },
  ];
};

const sizeStyles: (theme: Theme) => RootVariantsType = (theme) => {
  return [
    {
      props: { size: '2XS' },
      style: {
        borderRadius: '4px',
        height: '18px',
        padding: '0 4px',
        ...theme.typography.caption2.regular,
      },
    },
    {
      props: { size: 'XS' },
      style: {
        borderRadius: '4px',
        height: '24px',
        padding: '4px 8px',
        ...theme.typography.caption.regular,
      },
    },
    {
      props: { size: 'S' },
      style: {
        borderRadius: '8px',
        height: '32px',
        padding: '8px 12px',
        ...theme.typography.caption.regular,
      },
    },
    {
      props: { size: 'M' },
      style: {
        borderRadius: '8px',
        height: '40px',
        padding: '8px 16px',
        ...theme.typography.body2.regular,
      },
    },
  ];
};
